import { useEffect, useRef, useState } from 'react';
import { Search, Code2, Rocket, ArrowDown } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Search,
    phase: 'Weeks 1–4',
    title: 'Assessment & Strategy',
    description: 'We analyze your core banking infrastructure, FedNow connectivity, and go-to-market context. Together we co-design your sovereign payment roadmap — complete with revenue projections and a deployment timeline tailored to your institution.',
    deliverables: ['Infrastructure audit', 'Revenue modeling', 'Deployment roadmap', 'Pilot proposal'],
  },
  {
    number: '02',
    icon: Code2,
    phase: 'Months 1–6',
    title: 'Integration & Build',
    description: 'White-label deployment with your complete branding, connected to FedNow and Fedwire rails. Our documented API layers integrate directly with your Fiserv, Jack Henry, or FIS core — no rip-and-replace required.',
    deliverables: ['White-label configuration', 'Core banking integration', 'FedNow activation', 'BSA/AML compliance setup'],
  },
  {
    number: '03',
    icon: Rocket,
    phase: 'Months 6–9',
    title: 'Launch & Scale',
    description: 'Go live with your sovereign eWallet. Begin onboarding unbanked customers via SMS, activate merchant services, and watch non-interest income flow directly to your institution from day one.',
    deliverables: ['Live product launch', 'Unbanked onboarding', 'Merchant activation', 'Revenue reporting dashboard'],
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const stepRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!revealed) return;
    const timers = steps.map((_, i) =>
      setTimeout(() => setActiveStep(i), 400 + i * 400)
    );
    return () => timers.forEach(clearTimeout);
  }, [revealed]);

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      ref={sectionRef}
      className="relative section-padding bg-black"
      style={{ scrollMarginTop: '80px' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease',
          }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="green-line" />
            <span className="text-[#00FF00] font-mono text-xs uppercase tracking-widest">Deployment Process</span>
            <div className="green-line" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-tight"
            style={{ fontFamily: 'Unbounded, sans-serif' }}>
            From Pilot to Live
            <br />
            <span className="text-[#00FF00]">In 6–9 Months</span>
          </h2>
          <p className="text-[#A3A3A3] text-base mt-4 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
            A structured, low-risk deployment process designed for regulated financial institutions. No disruption to existing operations.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-[#1A1A1A] md:-translate-x-px" />

          {/* Active line overlay */}
          <div
            className="absolute left-6 md:left-1/2 top-0 w-px bg-[#00FF00] md:-translate-x-px transition-all duration-1000"
            style={{
              height: revealed ? '100%' : '0%',
              boxShadow: '0 0 10px rgba(0,255,0,0.5)',
              transitionDelay: '0.3s',
            }}
          />

          <div className="flex flex-col gap-16">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isActive = activeStep >= i;
              const isEven = i % 2 === 0;

              return (
                <div
                  key={step.number}
                  ref={(el) => (stepRefs.current[i] = el)}
                  data-testid={`step-${i}`}
                  className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${
                    isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  style={{
                    opacity: isActive ? 1 : 0.3,
                    transition: 'opacity 0.5s ease',
                  }}
                >
                  {/* Node */}
                  <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 z-10">
                    <div
                      className="w-12 h-12 flex items-center justify-center border-2 transition-all duration-500"
                      style={{
                        background: isActive ? '#00FF00' : '#0A0A0A',
                        borderColor: isActive ? '#00FF00' : '#1A1A1A',
                        boxShadow: isActive ? '0 0 20px rgba(0,255,0,0.4), 0 0 40px rgba(0,255,0,0.2)' : 'none',
                      }}
                    >
                      <Icon size={20} color={isActive ? '#000' : '#A3A3A3'} strokeWidth={2} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`ml-20 md:ml-0 md:w-5/12 ${isEven ? 'md:pr-16 md:text-right' : 'md:pl-16 md:text-left md:ml-auto'}`}>
                    <div
                      className="border border-[#1A1A1A] p-6 relative"
                      style={{
                        background: '#0A0A0A',
                        borderColor: isActive ? '#00FF00' : '#1A1A1A',
                        boxShadow: isActive ? '0 0 20px rgba(0,255,0,0.05)' : 'none',
                        transition: 'all 0.5s ease',
                      }}
                    >
                      <div className={`flex items-center gap-3 mb-4 ${isEven ? 'md:flex-row-reverse' : 'flex-row'}`}>
                        <span className="text-[#00FF00] font-mono text-xs uppercase tracking-widest bg-[#00FF00] bg-opacity-10 px-2 py-1">
                          {step.phase}
                        </span>
                        <span className="text-[#A3A3A3] font-mono text-4xl font-black opacity-20">{step.number}</span>
                      </div>
                      <h3 className="text-white font-bold mb-3 uppercase"
                        style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '14px', lineHeight: '1.4' }}>
                        {step.title}
                      </h3>
                      <p className="text-[#A3A3A3] text-sm leading-relaxed mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {step.description}
                      </p>
                      <div className={`flex flex-wrap gap-2 ${isEven ? 'md:justify-end' : 'justify-start'}`}>
                        {step.deliverables.map((d) => (
                          <span key={d} className="text-xs font-mono text-[#00FF00] border border-[#00FF00] border-opacity-30 px-2 py-1 bg-opacity-5"
                            style={{ background: 'rgba(0,255,0,0.05)' }}>
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className="text-center mt-20"
          style={{
            opacity: revealed ? 1 : 0,
            transition: 'opacity 0.7s ease 1.5s',
          }}
        >
          <p className="text-[#A3A3A3] text-sm mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to start your assessment? Our team works alongside your institution every step of the way.
          </p>
          <button
            onClick={() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="how-it-works-cta-btn"
            className="btn-neon text-xs"
            style={{ fontFamily: 'Unbounded, sans-serif', padding: '14px 32px' }}
          >
            Begin Your Assessment
          </button>
        </div>
      </div>
    </section>
  );
}
